import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { currentUserId } from '@/lib/currentUser';
import { AppLayout } from '@/components/layout/AppLayout';
import { PageHeader } from '@/components/ui/page-header';
import { DataTable } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { Plus, Mail, Phone, MapPin, Building2, User, Home, Trash2, MoreVertical, Edit, Eye, FileText, Search, Loader2, X } from 'lucide-react';
import { ImportTenantsDialog } from '@/components/tenants/ImportTenantsDialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { validatePostalCode, validateSiret } from '@/lib/validators';

type PropertyAssignment = {
  localId: string;
  propertyId: string;
  bureauSurface: string;
  entrepotSurface: string;
};

export default function Tenants() {
  const [isOpen, setIsOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<any>(null);
  const [editingTenant, setEditingTenant] = useState<any>(null);
  const [postalCodeError, setPostalCodeError] = useState<string>();
  const [tenantType, setTenantType] = useState<'individual' | 'professional'>('individual');
  const [siretError, setSiretError] = useState<string>();
  const [selectedSciId, setSelectedSciId] = useState<string>('');
  const [propertyAssignments, setPropertyAssignments] = useState<PropertyAssignment[]>([
    { localId: crypto.randomUUID(), propertyId: '', bureauSurface: '', entrepotSurface: '' },
  ]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const queryClient = useQueryClient();

  // Fetch all properties for dropdowns
  const { data: allProperties = [] } = useQuery({
    queryKey: ['all-properties'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('properties')
        .select('id, name, address, type, floor, area_sqm, sci_id')
        .order('address');
      if (error) throw error;
      return data;
    },
  });

  // Fetch tenant_property_links for display
  const { data: tenantPropertyLinks = [] } = useQuery({
    queryKey: ['tenant-property-links'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tenant_property_links' as any)
        .select('*');
      if (error) throw error;
      return data as any[];
    },
  });

  // Helper: get properties for a given SCI
  const getPropertiesForSci = (sciId: string) =>
    allProperties.filter((p: any) => p.sci_id === sciId);

  // Use the secure view that masks sensitive data for non-admin users
  const { data: tenants = [], isLoading } = useQuery({
    queryKey: ['tenants'],
    queryFn: async () => {
      // First get tenants from secure view
      const { data: tenantsData, error: tenantsError } = await supabase
        .from('tenants_secure' as any)
        .select('*')
        .order('created_at', { ascending: false });
      if (tenantsError) throw tenantsError;

      // Get clients and scis for display
      const clientIds = [...new Set(tenantsData?.map((t: any) => t.client_id).filter(Boolean))];
      const sciIds = [...new Set(tenantsData?.map((t: any) => t.sci_id).filter(Boolean))];

      const [clientsResult, scisResult] = await Promise.all([
        clientIds.length > 0 
          ? supabase.from('clients').select('id, name').in('id', clientIds)
          : { data: [] },
        sciIds.length > 0 
          ? supabase.from('scis').select('id, name').in('id', sciIds)
          : { data: [] }
      ]);

      const clientsMap = new Map((clientsResult.data || []).map((c: any) => [c.id, c]));
      const scisMap = new Map((scisResult.data || []).map((s: any) => [s.id, s]));

      return tenantsData?.map((t: any) => ({
        ...t,
        clients: clientsMap.get(t.client_id) || null,
        scis: scisMap.get(t.sci_id) || null
      })) || [];
    },
  });

  const { data: clients = [] } = useQuery({
    queryKey: ['clients'],
    queryFn: async () => {
      const { data, error } = await supabase.from('clients').select('id, name');
      if (error) throw error;
      return data;
    },
  });

  const { data: scis = [] } = useQuery({
    queryKey: ['scis'],
    queryFn: async () => {
      const { data, error } = await supabase.from('scis').select('id, name, client_id');
      if (error) throw error;
      return data;
    },
  });

  // Load property assignments when editing a tenant
  useEffect(() => {
    if (editingTenant?.id) {
      const links = tenantPropertyLinks.filter((l: any) => l.tenant_id === editingTenant.id);
      if (links.length > 0) {
        setPropertyAssignments(links.map((l: any) => ({
          localId: crypto.randomUUID(),
          propertyId: l.property_id || '',
          bureauSurface: l.bureau_surface?.toString() || '',
          entrepotSurface: l.entrepot_surface?.toString() || '',
        })));
      } else {
        setPropertyAssignments([
          { localId: crypto.randomUUID(), propertyId: '', bureauSurface: '', entrepotSurface: '' },
        ]);
      }
      setSelectedSciId(editingTenant.sci_id || '');
    }
  }, [editingTenant?.id]);

  const createTenant = useMutation({
    mutationFn: async (formData: FormData) => {
      const codePostalBien = formData.get('code_postal_bien') as string;
      if (codePostalBien) {
        const v = validatePostalCode(codePostalBien);
        if (!v.valid) throw new Error('Code postal bien: ' + v.message);
      }
      const codePostalLocataire = formData.get('code_postal_locataire') as string;
      if (codePostalLocataire) {
        const v = validatePostalCode(codePostalLocataire);
        if (!v.valid) throw new Error('Code postal locataire: ' + v.message);
      }

      const type = formData.get('tenant_type') as string;
      const siret = formData.get('siret') as string;

      if (type === 'professional' && siret) {
        const siretValidation = validateSiret(siret);
        if (!siretValidation.valid) {
          throw new Error(siretValidation.message);
        }
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', await currentUserId())
        .single();

      if (!profile?.organization_id) throw new Error('No organization');

      const sciId = formData.get('sci_id') as string;

      if (!sciId) throw new Error('Veuillez sélectionner une SCI');

      // Get client_id from the selected SCI - fetch from database to ensure we have the latest data
      const { data: selectedSci, error: sciError } = await supabase
        .from('scis')
        .select('id, client_id')
        .eq('id', sciId)
        .single();

      if (sciError || !selectedSci) {
        throw new Error('SCI non trouvée ou erreur lors de la récupération');
      }

      if (!selectedSci.client_id) {
        throw new Error('La SCI sélectionnée n\'a pas de client associé');
      }

      // Build notes
      const notes = formData.get('notes') as string || '';

      const { data: newTenant, error } = await supabase.from('tenants').insert({
        organization_id: profile.organization_id,
        client_id: selectedSci.client_id,
        sci_id: sciId,
        tenant_type: type,
        first_name: formData.get('first_name') as string,
        last_name: formData.get('last_name') as string,
        company_name: type === 'professional' ? formData.get('company_name') as string || null : null,
        siret: type === 'professional' ? siret || null : null,
        vat_number: type === 'professional' ? formData.get('vat_number') as string || null : null,
        email: formData.get('email') as string || null,
        phone: formData.get('phone') as string || null,
        adresse_bien: formData.get('adresse_bien') as string || null,
        code_postal_bien: codePostalBien || null,
        ville_bien: formData.get('ville_bien') as string || null,
        adresse_locataire: formData.get('adresse_locataire') as string || null,
        code_postal_locataire: codePostalLocataire || null,
        ville_locataire: formData.get('ville_locataire') as string || null,
        notes: notes.trim() || null,
      }).select('id').single();
      if (error) throw error;

      // Insert property assignments
      const validAssignments = propertyAssignments.filter(a => a.propertyId);
      if (validAssignments.length > 0 && newTenant) {
        const { error: linkError } = await supabase.from('tenant_property_links' as any).insert(
          validAssignments.map(a => ({
            organization_id: profile.organization_id,
            tenant_id: newTenant.id,
            property_id: a.propertyId,
            sci_id: sciId,
            bureau_surface: a.bureauSurface ? parseFloat(a.bureauSurface) : null,
            entrepot_surface: a.entrepotSurface ? parseFloat(a.entrepotSurface) : null,
          }))
        );
        if (linkError) console.error('Error creating property links:', linkError);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-property-links'] });
      setIsOpen(false);
      resetForm();
      toast.success('Locataire créé avec succès');
    },
    onError: (error: any) => {
      toast.error(error.message);
    },
  });

  const updateTenant = useMutation({
    mutationFn: async (formData: FormData) => {
      if (!editingTenant?.id) throw new Error('No tenant selected');

      const codePostalBien = formData.get('code_postal_bien') as string;
      if (codePostalBien) {
        const v = validatePostalCode(codePostalBien);
        if (!v.valid) throw new Error('Code postal bien: ' + v.message);
      }
      const codePostalLocataire = formData.get('code_postal_locataire') as string;
      if (codePostalLocataire) {
        const v = validatePostalCode(codePostalLocataire);
        if (!v.valid) throw new Error('Code postal locataire: ' + v.message);
      }

      const type = formData.get('tenant_type') as string;
      const siret = formData.get('siret') as string;

      if (type === 'professional' && siret) {
        const siretValidation = validateSiret(siret);
        if (!siretValidation.valid) {
          throw new Error(siretValidation.message);
        }
      }

      const sciId = formData.get('sci_id') as string;
      if (!sciId) throw new Error('Veuillez sélectionner une SCI');

      // Get client_id from the selected SCI
      const { data: selectedSci, error: sciError } = await supabase
        .from('scis')
        .select('id, client_id')
        .eq('id', sciId)
        .single();

      if (sciError || !selectedSci) {
        throw new Error('SCI non trouvée ou erreur lors de la récupération');
      }

      if (!selectedSci.client_id) {
        throw new Error('La SCI sélectionnée n\'a pas de client associé');
      }

      // Build notes
      const notes = formData.get('notes') as string || '';

      const { error } = await supabase
        .from('tenants')
        .update({
          client_id: selectedSci.client_id,
          sci_id: sciId,
          tenant_type: type,
          first_name: formData.get('first_name') as string,
          last_name: formData.get('last_name') as string,
          company_name: type === 'professional' ? formData.get('company_name') as string || null : null,
          siret: type === 'professional' ? siret || null : null,
          vat_number: type === 'professional' ? formData.get('vat_number') as string || null : null,
          email: formData.get('email') as string || null,
          phone: formData.get('phone') as string || null,
          adresse_bien: formData.get('adresse_bien') as string || null,
          code_postal_bien: codePostalBien || null,
          ville_bien: formData.get('ville_bien') as string || null,
          adresse_locataire: formData.get('adresse_locataire') as string || null,
          code_postal_locataire: codePostalLocataire || null,
          ville_locataire: formData.get('ville_locataire') as string || null,
          notes: notes.trim() || null,
        })
        .eq('id', editingTenant.id);

      if (error) throw error;

      // Update property links: delete old, insert new
      await supabase.from('tenant_property_links' as any).delete().eq('tenant_id', editingTenant.id);
      const validAssignments = propertyAssignments.filter(a => a.propertyId);
      if (validAssignments.length > 0) {
        const { data: profileData } = await supabase.from('profiles').select('organization_id').eq('user_id', await currentUserId()).single();
        const { error: linkError } = await supabase.from('tenant_property_links' as any).insert(
          validAssignments.map(a => ({
            organization_id: profileData?.organization_id,
            tenant_id: editingTenant.id,
            property_id: a.propertyId,
            sci_id: sciId,
            bureau_surface: a.bureauSurface ? parseFloat(a.bureauSurface) : null,
            entrepot_surface: a.entrepotSurface ? parseFloat(a.entrepotSurface) : null,
          }))
        );
        if (linkError) console.error('Error updating property links:', linkError);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-property-links'] });
      setEditingTenant(null);
      setIsEditOpen(false);
      setSelectedTenant(null);
      resetForm();
      toast.success('Locataire mis à jour avec succès');
    },
    onError: (error: any) => {
      toast.error(error.message);
    },
  });

  const deleteTenant = useMutation({
    mutationFn: async (tenantId: string) => {
      const { error } = await supabase
        .from('tenants')
        .delete()
        .eq('id', tenantId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-property-links'] });
      toast.success('Locataire supprimé avec succès');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Erreur lors de la suppression');
    },
  });

  const resetForm = () => {
    setPostalCodeError(undefined);
    setSiretError(undefined);
    setTenantType('individual');
    setSelectedSciId('');
    setPropertyAssignments([
      { localId: crypto.randomUUID(), propertyId: '', bureauSurface: '', entrepotSurface: '' },
    ]);
  };

  const handlePostalCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value) {
      const validation = validatePostalCode(value);
      setPostalCodeError(validation.valid ? undefined : validation.message);
    } else {
      setPostalCodeError(undefined);
    }
  };

  const handleSiretChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value) {
      const validation = validateSiret(value);
      setSiretError(validation.valid ? undefined : validation.message);
    } else {
      setSiretError(undefined);
    }
  };

  const columns = [
    {
      header: 'Type',
      accessorKey: (row: any) => (
        <Badge variant={row.tenant_type === 'professional' ? 'default' : 'secondary'}>
          {row.tenant_type === 'professional' ? (
            <><Building2 className="h-3 w-3 mr-1" /> Pro</>
          ) : (
            <><User className="h-3 w-3 mr-1" /> Particulier</>
          )}
        </Badge>
      ),
    },
    {
      header: 'Nom',
      accessorKey: (row: any) => {
        const fullName = `${row.first_name || ''} ${row.last_name || ''}`.trim() || '-';
        return (
          <div>
            {row.tenant_type === 'professional' && row.company_name && (
              <div className="font-medium">{row.company_name}</div>
            )}
            <div className={row.tenant_type === 'professional' && row.company_name ? 'text-sm text-muted-foreground' : 'font-medium'}>
              {fullName}
            </div>
          </div>
        );
      },
    },
    { header: 'SCI', accessorKey: (row: any) => row.scis?.name || '-' },
    {
      header: 'Bien(s) assigné(s)',
      accessorKey: (row: any) => {
        const links = tenantPropertyLinks.filter((l: any) => l.tenant_id === row.id);
        if (links.length === 0) return '-';
        return (
          <div className="flex flex-col gap-1">
            {links.map((link: any) => {
              const prop = allProperties.find((p: any) => p.id === link.property_id);
              return prop ? (
                <div key={link.id} className="flex items-center gap-1 text-sm">
                  <Home className="h-3 w-3" />
                  {prop.name || prop.address}
                </div>
              ) : null;
            })}
          </div>
        );
      },
    },
    {
      header: 'Contact',
      accessorKey: (row: any) => (
        <div className="flex flex-col gap-1">
          {row.email && <span className="flex items-center gap-1 text-sm"><Mail className="h-3 w-3" /> {row.email}</span>}
          {row.phone && <span className="flex items-center gap-1 text-sm"><Phone className="h-3 w-3" /> {row.phone}</span>}
        </div>
      ),
    },
    {
      header: 'Adresse du bien',
      accessorKey: (row: any) => (
        <div className="flex flex-col gap-1">
          {row.adresse_bien && <span className="flex items-center gap-1 text-sm"><Home className="h-3 w-3" /> {row.adresse_bien}</span>}
          {(row.code_postal_bien || row.ville_bien) && (
            <span className="text-sm text-muted-foreground ml-4">{row.code_postal_bien} {row.ville_bien}</span>
          )}
        </div>
      ),
    },
    {
      header: 'Adresse locataire',
      accessorKey: (row: any) => (
        <div className="flex flex-col gap-1">
          {row.adresse_locataire && <span className="flex items-center gap-1 text-sm"><MapPin className="h-3 w-3" /> {row.adresse_locataire}</span>}
          {(row.code_postal_locataire || row.ville_locataire) && (
            <span className="text-sm text-muted-foreground ml-4">{row.code_postal_locataire} {row.ville_locataire}</span>
          )}
        </div>
      ),
    },
    {
      header: 'Actions',
      accessorKey: (row: any) => {
        const tenantName = row.tenant_type === 'professional' && row.company_name
          ? row.company_name
          : `${row.first_name || ''} ${row.last_name || ''}`.trim();

        return (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedTenant(row)}
              className="gap-2"
            >
              <Eye className="h-4 w-4" />
              Détails
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => {
                  setEditingTenant(row);
                  setTenantType(row.tenant_type);
                  setSelectedSciId(row.sci_id || '');
                  setIsEditOpen(true);
                }}>
                  <Edit className="mr-2 h-4 w-4" />
                  Modifier
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="text-destructive">
                      <Trash2 className="mr-2 h-4 w-4" />
                      Supprimer
                    </DropdownMenuItem>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Supprimer le locataire</AlertDialogTitle>
                      <AlertDialogDescription>
                        Êtes-vous sûr de vouloir supprimer le locataire "{tenantName || 'sans nom'}" ? Cette action est irréversible et supprimera également tous les contrats associés.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Annuler</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => deleteTenant.mutate(row.id)}
                        disabled={deleteTenant.isPending}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        {deleteTenant.isPending ? 'Suppression...' : 'Supprimer'}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ];

  const filteredTenants = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return tenants;
    return tenants.filter((t: any) => {
      const firstName = (t.first_name || '').toLowerCase();
      const lastName = (t.last_name || '').toLowerCase();
      const companyName = (t.company_name || '').toLowerCase();
      const email = (t.email || '').toLowerCase();
      const phone = (t.phone || '').toLowerCase();
      const city = (t.ville_locataire || '').toLowerCase();
      return firstName.includes(term) || lastName.includes(term) || companyName.includes(term) || email.includes(term) || phone.includes(term) || city.includes(term);
    });
  }, [tenants, searchTerm]);

  return (
    <AppLayout title="Locataires" description="Gérez vos locataires">
      <PageHeader
        title="Locataires"
        description={`${tenants.length} locataire${tenants.length > 1 ? 's' : ''}`}
        actions={
          <div className="flex gap-2">
            <ImportTenantsDialog />
            <Dialog open={isOpen} onOpenChange={(open) => {
              setIsOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button><Plus className="mr-2 h-4 w-4" /> Nouveau locataire</Button>
              </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Nouveau locataire</DialogTitle>
              </DialogHeader>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  createTenant.mutate(new FormData(e.currentTarget));
                }}
                className="space-y-4"
              >
                {/* Tenant Type Selection */}
                <div className="space-y-2">
                  <Label>Type de locataire *</Label>
                  <Select 
                    name="tenant_type" 
                    value={tenantType} 
                    onValueChange={(v) => setTenantType(v as 'individual' | 'professional')}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="individual">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4" />
                          Particulier
                        </div>
                      </SelectItem>
                      <SelectItem value="professional">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4" />
                          Professionnel
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Professional Fields */}
                {tenantType === 'professional' && (
                  <div className="space-y-4 p-4 bg-muted rounded-lg">
                    <div className="space-y-2">
                      <Label htmlFor="company_name">Raison sociale *</Label>
                      <Input id="company_name" name="company_name" required placeholder="Nom de l'entreprise" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="siret">SIRET</Label>
                        <Input 
                          id="siret" 
                          name="siret" 
                          maxLength={14} 
                          placeholder="12345678901234"
                          onChange={handleSiretChange}
                        />
                        {siretError && <p className="text-sm text-destructive">{siretError}</p>}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="vat_number">N° TVA intracommunautaire</Label>
                        <Input id="vat_number" name="vat_number" placeholder="FR12345678901" />
                      </div>
                    </div>
                  </div>
                )}

                {/* SCI and Property Assignments */}
                <div className="space-y-4 p-4 bg-primary/5 rounded-lg border border-primary/20">
                  <h4 className="font-medium flex items-center gap-2">
                    <Home className="h-4 w-4" />
                    Assignation au(x) bien(s)
                  </h4>
                  
                  <div className="space-y-2">
                    <Label>SCI *</Label>
                    <Select 
                      name="sci_id" 
                      required 
                      value={selectedSciId} 
                      onValueChange={(v) => {
                        setSelectedSciId(v);
                        setPropertyAssignments([
                          { localId: crypto.randomUUID(), propertyId: '', bureauSurface: '', entrepotSurface: '' },
                        ]);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner une SCI" />
                      </SelectTrigger>
                      <SelectContent>
                        {scis.map((sci) => (
                          <SelectItem key={sci.id} value={sci.id}>{sci.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedSciId && propertyAssignments.map((assignment, idx) => {
                    const sciProperties = getPropertiesForSci(selectedSciId);
                    const selectedProp = sciProperties.find((p: any) => p.id === assignment.propertyId);
                    return (
                      <div key={assignment.localId} className="space-y-3 p-3 border rounded-lg relative">
                        {propertyAssignments.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute top-1 right-1 h-6 w-6 p-0"
                            onClick={() => setPropertyAssignments(prev => prev.filter(a => a.localId !== assignment.localId))}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        )}
                        <div className="space-y-2">
                          <Label>Bien {idx + 1}</Label>
                          <Select
                            value={assignment.propertyId}
                            onValueChange={(v) => setPropertyAssignments(prev => prev.map(a => a.localId === assignment.localId ? { ...a, propertyId: v } : a))}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Sélectionner un bien" />
                            </SelectTrigger>
                            <SelectContent>
                              {sciProperties.map((property: any) => (
                                <SelectItem key={property.id} value={property.id}>
                                  <span>{property.name || property.address}</span>
                                  {property.floor && <span className="text-xs text-muted-foreground ml-1">Lot: {property.floor}</span>}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        {selectedProp && (
                          <>
                            <div className="text-sm text-muted-foreground">
                              <p>Type: {selectedProp.type || 'Non défini'}</p>
                              {selectedProp.floor && <p>Lot/Référence: {selectedProp.floor}</p>}
                              {selectedProp.area_sqm && <p>Surface totale: {selectedProp.area_sqm} m²</p>}
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label>Surface bureau (m²)</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  placeholder="0"
                                  value={assignment.bureauSurface}
                                  onChange={(e) => setPropertyAssignments(prev => prev.map(a => a.localId === assignment.localId ? { ...a, bureauSurface: e.target.value } : a))}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>Surface entrepôt (m²)</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  placeholder="0"
                                  value={assignment.entrepotSurface}
                                  onChange={(e) => setPropertyAssignments(prev => prev.map(a => a.localId === assignment.localId ? { ...a, entrepotSurface: e.target.value } : a))}
                                />
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}

                  {selectedSciId && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setPropertyAssignments(prev => [...prev, { localId: crypto.randomUUID(), propertyId: '', bureauSurface: '', entrepotSurface: '' }])}
                    >
                      <Plus className="h-3 w-3 mr-1" /> Ajouter un bien
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">Prénom {tenantType === 'professional' ? '(contact)' : '*'}</Label>
                    <Input id="first_name" name="first_name" required={tenantType === 'individual'} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name">Nom {tenantType === 'professional' ? '(contact)' : '*'}</Label>
                    <Input id="last_name" name="last_name" required={tenantType === 'individual'} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" name="email" type="email" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Téléphone</Label>
                    <Input id="phone" name="phone" />
                  </div>
                </div>

                <p className="text-sm font-semibold text-muted-foreground pt-2">Adresse du bien</p>
                <div className="space-y-2">
                  <Label htmlFor="adresse_bien">Adresse</Label>
                  <Input id="adresse_bien" name="adresse_bien" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="code_postal_bien">Code postal</Label>
                    <Input id="code_postal_bien" name="code_postal_bien" maxLength={5} placeholder="75001" onChange={handlePostalCodeChange} />
                    {postalCodeError && <p className="text-sm text-destructive">{postalCodeError}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ville_bien">Ville</Label>
                    <Input id="ville_bien" name="ville_bien" />
                  </div>
                </div>

                <p className="text-sm font-semibold text-muted-foreground pt-2">Adresse du locataire</p>
                <div className="space-y-2">
                  <Label htmlFor="adresse_locataire">Adresse</Label>
                  <Input id="adresse_locataire" name="adresse_locataire" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="code_postal_locataire">Code postal</Label>
                    <Input id="code_postal_locataire" name="code_postal_locataire" maxLength={5} placeholder="75001" onChange={handlePostalCodeChange} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ville_locataire">Ville</Label>
                    <Input id="ville_locataire" name="ville_locataire" />
                  </div>
                </div>

                <input type="hidden" name="notes" value="" />

                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={createTenant.isPending || !!postalCodeError || !!siretError || !selectedSciId}
                >
                  {createTenant.isPending ? 'Création...' : 'Créer le locataire'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        }
      />

      {/* Search */}
      <div className="flex items-center gap-4 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher un locataire..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8"
          />
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filteredTenants}
        isLoading={isLoading}
        emptyMessage="Aucun locataire. Créez d'abord une SCI avec des biens, puis ajoutez des locataires."
        selectable
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
      />

      {selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-background border rounded-lg shadow-lg px-4 py-3 flex items-center gap-4">
          <span className="text-sm font-medium">{selectedIds.size} sélectionné(s)</span>
          <AlertDialog open={bulkDeleteOpen} onOpenChange={(open) => !bulkDeleteLoading && setBulkDeleteOpen(open)}>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" size="sm"><Trash2 className="h-4 w-4 mr-2" /> Supprimer</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Supprimer {selectedIds.size} locataire(s) ?</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-2">
                    <p>Cette action est <strong>irréversible</strong>.</p>
                    <p className="text-destructive font-medium">⚠️ Toutes les données liées seront supprimées :</p>
                    <ul className="list-disc list-inside text-sm space-y-1">
                      <li>Contrats de location associés</li>
                      <li>Périodes de loyer, paiements et allocations</li>
                      <li>Quittances et appels de fonds</li>
                    </ul>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={bulkDeleteLoading}>Annuler</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  disabled={bulkDeleteLoading}
                  onClick={async () => {
                    setBulkDeleteLoading(true);
                    let successCount = 0;
                    const totalCount = selectedIds.size;
                    try {
                      for (const id of selectedIds) {
                        try {
                          const { error } = await supabase.from('tenants').delete().eq('id', id);
                          if (error) throw error;
                          successCount++;
                        } catch (err: any) {
                          console.error(`Échec suppression locataire ${id}:`, err);
                          toast.error(`Erreur sur un locataire : ${err.message || 'Erreur inconnue'}`);
                        }
                      }
                      queryClient.invalidateQueries({ queryKey: ['tenants'] });
                      queryClient.invalidateQueries({ queryKey: ['tenant-property-links'] });
                      queryClient.invalidateQueries({ queryKey: ['leases'] });
                      queryClient.invalidateQueries({ queryKey: ['receipts'] });
                      queryClient.invalidateQueries({ queryKey: ['call_for_funds'] });
                      queryClient.invalidateQueries({ queryKey: ['payments'] });
                      queryClient.invalidateQueries({ queryKey: ['rent_periods'] });
                      if (successCount === totalCount) {
                        toast.success(`${successCount} locataire(s) supprimé(s)`);
                      } else if (successCount > 0) {
                        toast.warning(`${successCount}/${totalCount} locataire(s) supprimé(s). ${totalCount - successCount} erreur(s).`);
                      }
                      setSelectedIds(new Set());
                    } finally {
                      setBulkDeleteLoading(false);
                      setBulkDeleteOpen(false);
                    }
                  }}
                >
                  {bulkDeleteLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Suppression...</> : 'Supprimer'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button variant="outline" size="sm" onClick={() => setSelectedIds(new Set())} disabled={bulkDeleteLoading}>Annuler</Button>
        </div>
      )}

      {/* Details Sheet */}
      <Sheet open={!!selectedTenant} onOpenChange={() => setSelectedTenant(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          {selectedTenant && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  {selectedTenant.tenant_type === 'professional' ? (
                    <><Building2 className="h-5 w-5" /> {selectedTenant.company_name || 'Professionnel'}</>
                  ) : (
                    <><User className="h-5 w-5" /> {`${selectedTenant.first_name || ''} ${selectedTenant.last_name || ''}`.trim()}</>
                  )}
                </SheetTitle>
              </SheetHeader>
              <div className="space-y-4 mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Type de locataire</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Badge variant={selectedTenant.tenant_type === 'professional' ? 'default' : 'secondary'}>
                      {selectedTenant.tenant_type === 'professional' ? (
                        <><Building2 className="h-3 w-3 mr-1" /> Professionnel</>
                      ) : (
                        <><User className="h-3 w-3 mr-1" /> Particulier</>
                      )}
                    </Badge>
                  </CardContent>
                </Card>

                {selectedTenant.tenant_type === 'professional' && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Informations société</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <p className="text-sm text-muted-foreground">Raison sociale</p>
                        <p className="font-medium">{selectedTenant.company_name || '-'}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-muted-foreground">SIRET</p>
                          <p className="font-medium">{selectedTenant.siret || '-'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">N° TVA</p>
                          <p className="font-medium">{selectedTenant.vat_number || '-'}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Informations {selectedTenant.tenant_type === 'professional' ? 'contact' : 'personnelles'}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground">Prénom</p>
                        <p className="font-medium">{selectedTenant.first_name || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Nom</p>
                        <p className="font-medium">{selectedTenant.last_name || '-'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">SCI et Bien(s)</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <p className="text-sm text-muted-foreground">SCI</p>
                      <p className="font-medium">{selectedTenant.scis?.name || '-'}</p>
                    </div>
                    {(() => {
                      const links = tenantPropertyLinks.filter((l: any) => l.tenant_id === selectedTenant.id);
                      if (links.length === 0) return null;
                      return (
                        <div>
                          <p className="text-sm text-muted-foreground">Bien(s) assigné(s)</p>
                          <div className="space-y-2 mt-1">
                            {links.map((link: any) => {
                              const prop = allProperties.find((p: any) => p.id === link.property_id);
                              if (!prop) return null;
                              return (
                                <div key={link.id} className="flex items-start gap-2 text-sm">
                                  <Home className="h-3 w-3 mt-0.5 shrink-0" />
                                  <div>
                                    <p className="font-medium">{prop.name || prop.address}</p>
                                    {link.bureau_surface && <p className="text-muted-foreground">Bureau: {link.bureau_surface} m²</p>}
                                    {link.entrepot_surface && <p className="text-muted-foreground">Entrepôt: {link.entrepot_surface} m²</p>}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      Contact
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <p className="text-sm text-muted-foreground">Email</p>
                      <p className="font-medium">{selectedTenant.email || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Téléphone</p>
                      <p className="font-medium">{selectedTenant.phone || '-'}</p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Home className="h-4 w-4" />
                      Adresse du bien
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <p className="text-sm text-muted-foreground">Adresse</p>
                      <p className="font-medium">{selectedTenant.adresse_bien || '-'}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground">Code postal</p>
                        <p className="font-medium">{selectedTenant.code_postal_bien || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Ville</p>
                        <p className="font-medium">{selectedTenant.ville_bien || '-'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      Adresse du locataire
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <p className="text-sm text-muted-foreground">Adresse</p>
                      <p className="font-medium">{selectedTenant.adresse_locataire || '-'}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground">Code postal</p>
                        <p className="font-medium">{selectedTenant.code_postal_locataire || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Ville</p>
                        <p className="font-medium">{selectedTenant.ville_locataire || '-'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      setEditingTenant(selectedTenant);
                      setTenantType(selectedTenant.tenant_type);
                      setSelectedSciId(selectedTenant.sci_id || '');
                      setIsEditOpen(true);
                    }}
                    className="flex-1"
                  >
                    <Edit className="mr-2 h-4 w-4" />
                    Modifier
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={(open) => {
        setIsEditOpen(open);
        if (!open) {
          setEditingTenant(null);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifier le locataire</DialogTitle>
          </DialogHeader>
          {editingTenant && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                updateTenant.mutate(new FormData(e.currentTarget));
              }}
              className="space-y-4"
            >
              {/* Tenant Type Selection */}
              <div className="space-y-2">
                <Label>Type de locataire *</Label>
                <Select
                  name="tenant_type"
                  value={tenantType}
                  onValueChange={(v) => setTenantType(v as 'individual' | 'professional')}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="individual">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        Particulier
                      </div>
                    </SelectItem>
                    <SelectItem value="professional">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        Professionnel
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Professional Fields */}
              {tenantType === 'professional' && (
                <div className="space-y-4 p-4 bg-muted rounded-lg">
                  <div className="space-y-2">
                    <Label htmlFor="edit-company_name">Raison sociale *</Label>
                    <Input
                      id="edit-company_name"
                      name="company_name"
                      defaultValue={editingTenant.company_name || ''}
                      required
                      placeholder="Nom de l'entreprise"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-siret">SIRET</Label>
                      <Input
                        id="edit-siret"
                        name="siret"
                        maxLength={14}
                        placeholder="12345678901234"
                        defaultValue={editingTenant.siret || ''}
                        onChange={handleSiretChange}
                      />
                      {siretError && <p className="text-sm text-destructive">{siretError}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-vat_number">N° TVA intracommunautaire</Label>
                      <Input
                        id="edit-vat_number"
                        name="vat_number"
                        placeholder="FR12345678901"
                        defaultValue={editingTenant.vat_number || ''}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* SCI and Property Assignments */}
              <div className="space-y-4 p-4 bg-primary/5 rounded-lg border border-primary/20">
                <h4 className="font-medium flex items-center gap-2">
                  <Home className="h-4 w-4" />
                  Assignation au(x) bien(s)
                </h4>

                <div className="space-y-2">
                  <Label>SCI *</Label>
                  <Select
                    name="sci_id"
                    required
                    value={selectedSciId}
                    onValueChange={(v) => {
                      setSelectedSciId(v);
                      setPropertyAssignments([
                        { localId: crypto.randomUUID(), propertyId: '', bureauSurface: '', entrepotSurface: '' },
                      ]);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner une SCI" />
                    </SelectTrigger>
                    <SelectContent>
                      {scis.map((sci) => (
                        <SelectItem key={sci.id} value={sci.id}>{sci.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedSciId && propertyAssignments.map((assignment, idx) => {
                  const sciProperties = getPropertiesForSci(selectedSciId);
                  const selectedProp = sciProperties.find((p: any) => p.id === assignment.propertyId);
                  return (
                    <div key={assignment.localId} className="space-y-3 p-3 border rounded-lg relative">
                      {propertyAssignments.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute top-1 right-1 h-6 w-6 p-0"
                          onClick={() => setPropertyAssignments(prev => prev.filter(a => a.localId !== assignment.localId))}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      )}
                      <div className="space-y-2">
                        <Label>Bien {idx + 1}</Label>
                        <Select
                          value={assignment.propertyId}
                          onValueChange={(v) => setPropertyAssignments(prev => prev.map(a => a.localId === assignment.localId ? { ...a, propertyId: v } : a))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner un bien" />
                          </SelectTrigger>
                          <SelectContent>
                            {sciProperties.map((property: any) => (
                              <SelectItem key={property.id} value={property.id}>
                                <span>{property.name || property.address}</span>
                                {property.floor && <span className="text-xs text-muted-foreground ml-1">Lot: {property.floor}</span>}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {selectedProp && (
                        <>
                          <div className="text-sm text-muted-foreground">
                            <p>Type: {selectedProp.type || 'Non défini'}</p>
                            {selectedProp.floor && <p>Lot/Référence: {selectedProp.floor}</p>}
                            {selectedProp.area_sqm && <p>Surface totale: {selectedProp.area_sqm} m²</p>}
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Surface bureau (m²)</Label>
                              <Input
                                type="number"
                                step="0.01"
                                placeholder="0"
                                value={assignment.bureauSurface}
                                onChange={(e) => setPropertyAssignments(prev => prev.map(a => a.localId === assignment.localId ? { ...a, bureauSurface: e.target.value } : a))}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Surface entrepôt (m²)</Label>
                              <Input
                                type="number"
                                step="0.01"
                                placeholder="0"
                                value={assignment.entrepotSurface}
                                onChange={(e) => setPropertyAssignments(prev => prev.map(a => a.localId === assignment.localId ? { ...a, entrepotSurface: e.target.value } : a))}
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}

                {selectedSciId && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setPropertyAssignments(prev => [...prev, { localId: crypto.randomUUID(), propertyId: '', bureauSurface: '', entrepotSurface: '' }])}
                  >
                    <Plus className="h-3 w-3 mr-1" /> Ajouter un bien
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-first_name">Prénom {tenantType === 'professional' ? '(contact)' : '*'}</Label>
                  <Input
                    id="edit-first_name"
                    name="first_name"
                    defaultValue={editingTenant.first_name || ''}
                    required={tenantType === 'individual'}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-last_name">Nom {tenantType === 'professional' ? '(contact)' : '*'}</Label>
                  <Input
                    id="edit-last_name"
                    name="last_name"
                    defaultValue={editingTenant.last_name || ''}
                    required={tenantType === 'individual'}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-email">Email</Label>
                  <Input
                    id="edit-email"
                    name="email"
                    type="email"
                    defaultValue={editingTenant.email || ''}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-phone">Téléphone</Label>
                  <Input
                    id="edit-phone"
                    name="phone"
                    defaultValue={editingTenant.phone || ''}
                  />
                </div>
              </div>

              <p className="text-sm font-semibold text-muted-foreground pt-2">Adresse du bien</p>
              <div className="space-y-2">
                <Label htmlFor="edit-adresse_bien">Adresse</Label>
                <Input id="edit-adresse_bien" name="adresse_bien" defaultValue={editingTenant.adresse_bien || ''} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-code_postal_bien">Code postal</Label>
                  <Input id="edit-code_postal_bien" name="code_postal_bien" maxLength={5} placeholder="75001" defaultValue={editingTenant.code_postal_bien || ''} onChange={handlePostalCodeChange} />
                  {postalCodeError && <p className="text-sm text-destructive">{postalCodeError}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-ville_bien">Ville</Label>
                  <Input id="edit-ville_bien" name="ville_bien" defaultValue={editingTenant.ville_bien || ''} />
                </div>
              </div>

              <p className="text-sm font-semibold text-muted-foreground pt-2">Adresse du locataire</p>
              <div className="space-y-2">
                <Label htmlFor="edit-adresse_locataire">Adresse</Label>
                <Input id="edit-adresse_locataire" name="adresse_locataire" defaultValue={editingTenant.adresse_locataire || ''} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-code_postal_locataire">Code postal</Label>
                  <Input id="edit-code_postal_locataire" name="code_postal_locataire" maxLength={5} placeholder="75001" defaultValue={editingTenant.code_postal_locataire || ''} onChange={handlePostalCodeChange} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-ville_locataire">Ville</Label>
                  <Input id="edit-ville_locataire" name="ville_locataire" defaultValue={editingTenant.ville_locataire || ''} />
                </div>
              </div>

              <input type="hidden" name="notes" value="" />

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setIsEditOpen(false);
                    setEditingTenant(null);
                    resetForm();
                  }}
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={updateTenant.isPending || !!postalCodeError || !!siretError || !selectedSciId}
                >
                  {updateTenant.isPending ? 'Mise à jour...' : 'Mettre à jour'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
